import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

import {
  AutoComplete,
  DialogBox,
  FieldControl,
  Map,
} from "../../../shared/components";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { useNavigation } from "../../../hooks/useNavigation";
import { useProjectFilters } from "../../../hooks/useProjectFilters";

import { AssetHeaderProps } from "../types";
import { useAddProject } from "../index";
import SiteDialog from "./SiteDialog";
import { useFetchPortfoliosList } from "../../portfolio/hooks/useFetchPortfoliosList";
import { useInfiniteSites } from "../hooks/useSites";
import { AsyncAutoComplete } from "../../../shared/components/AsyncAutoComplete";
import { clientTheme } from "../../../theme/clientTheme";

// Constants
const REDIRECT_COUNTDOWN_SECONDS = 5;
const hiddenProjectFormFields = clientTheme.layout?.hiddenProjectFormFields ?? [];
const hideProjectAssetDetails = clientTheme.layout?.hideProjectAssetDetails ?? false;

// Shared Grid sizes for consistent layout
const SECTION_LABEL_SIZE = { xs: 12, sm: 12, md: 4, lg: 4 } as const;
const SECTION_CONTENT_SIZE = { xs: 12, sm: 12, md: 8, lg: 8 } as const;

// Helper Components
const AssetHeader: React.FC<AssetHeaderProps> = ({ title, onDelete }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    bgcolor="action.hover"
    py={1}
    px={2}
  >
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    <IconButton color="error" onClick={onDelete} size="small">
      <Delete />
    </IconButton>
  </Box>
);

// Converts various input types to a number array for filter operations
const normalizeToNumberArray = (
  value: string[] | number | string | null | undefined,
): number[] => {
  if (Array.isArray(value)) {
    return value.map(Number);
  }
  return value ? [Number(value)] : [];
};

export const AddProject: React.FC<{ isEdit: boolean }> = ({ isEdit }) => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();

  const programme = searchParams.get("programme");
  const portfolio = searchParams.get("portfolio");
  const portfolio_name = searchParams.get("portfolio_name");

  const { goBack, goToProjectListing } = useNavigation();

  const {
    formData,
    fieldErrors,
    assets,
    assetErrors,
    otherSite,
    isSubmitting,
    submitMessage,
    thirdPartySites,
    setOtherSite,
    getSiteOptions,
    handleAddAsset,
    handleRemoveAsset,
    handleSubmit,
    handleFieldChange,
    handleNetworkChange,
    handleSiteChange,
    handleOtherSiteChange,
    handleAddressChange,
    handleAssetChange,
    setSubmitMessage,
    setFormData,
    refetchThirdPartySites
  } = useAddProject({ isEdit, projectId });

  const { data: filteredPortfolios, isError: isPortfoliosError, isLoading: isPortfoliosLoading } = useFetchPortfoliosList();

  const { getFilterOptions } = useDisplayField([]);
  const { getFilteredData, cleanFilters, projectsFilterData } = useProjectFilters();

  const [search, setSearch] = useState("");

  // Use infinite sites with region filtering and third-party site toggle
  const { 
    sites, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage,
    isLoading: isSitesLoading,
    refetch: refetchSites,
  } = useInfiniteSites(search, formData.region, otherSite);

  // Reset search and pagination when region or otherSite changes
  useEffect(() => {
    setSearch("");
  }, [formData.region, otherSite]);

  const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_SECONDS);
  const [newSiteDialogOpen, setNewSiteDialogOpen] = useState(false);

  // Memoized filtered options based on current form data
  const { work_type_groups, work_types } = useMemo(
    () =>
      getFilteredData({
        workstream: formData.workstream
          ? normalizeToNumberArray(formData.workstream)
          : null,
        work_type_group: formData.work_type_group
          ? normalizeToNumberArray(formData.work_type_group)
          : null,
      }),
    [formData.workstream, formData.work_type_group, getFilteredData],
  );

  const currentSiteOption = React.useMemo(() => {
    if (!formData.site_name) return null;
    const fromLoaded = sites.find((s: any) => s.id === formData.site_name);
    if (fromLoaded) return { value: fromLoaded.id, label: fromLoaded.description };
    if (formData.site_description) {
      return { value: formData.site_name, label: formData.site_description };
    }
    return null;
  }, [formData.site_name, formData.site_description, sites]);

  // Handlers
  const handleFilterChange = useCallback(
    (key: "workstream" | "work_type_group", value: string[]) => {
      const updated = { ...formData, [key]: value };
      const cleaned = cleanFilters(updated as any, key, projectsFilterData);
      setFormData((prev) => ({
        ...prev,
        [key]: value,
        work_type_group:
          key === "workstream" ? null : (cleaned.work_type_group as any),
        work_type:
          key === "workstream" || key === "work_type_group"
            ? null
            : (cleaned.work_type as any),
      }));
    },
    [cleanFilters, formData, projectsFilterData, setFormData],
  );

  useEffect(() => {
    if (programme || portfolio) {
      setFormData((prev) => ({
        ...prev,
        programme: programme ? Number(programme) : prev.programme,
        portfolio: portfolio ? Number(portfolio) : prev.portfolio,
      }));
    }
  }, [programme, portfolio, setFormData]);

  useEffect(() => {
    if (isEdit && formData.region && formData.site_name) {
      // 1. Check if the site_id belongs to the third-party list
      const isThirdPartySite = thirdPartySites?.some(
        (site: any) => site.id === formData.site_name
      );

      const normalSites = getSiteOptions();
      const isNormalSite = normalSites.some(
        (site) => site.id === formData.site_name
      );

      if (isThirdPartySite && !otherSite) {
        setOtherSite(true);
      } else if (isNormalSite && otherSite) {
        setOtherSite(false);
      }
    }
  }, [
    isEdit,
    formData.site_name,
    thirdPartySites,
    otherSite
  ]);

  useEffect(() => {
    if (submitMessage) {
      setCountdown(5);
      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      const timeout = setTimeout(() => {
        if (isEdit) {
          goBack();
        } else {
          goToProjectListing();
        }
      }, 5000);
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [submitMessage]);

  if (submitMessage) {
    return (
      <Box
        m={2}
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Alert
          severity={submitMessage.type}
          onClose={() => setSubmitMessage(null)}
          sx={{ mb: 3 }}
        >
          {submitMessage.message} <br />
          Redirecting in {countdown} seconds...
        </Alert>
      </Box>
    );
  }

  if (isPortfoliosError) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <Alert severity="error">Failed to load portfolios. Please try again later.</Alert>
      </Box>
    )
  };

  if (isPortfoliosLoading) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    )
  };

  return (
    <Box p={2}>
      {/* Project Information Section */}
      <Grid container spacing={3}>
        <Grid size={SECTION_LABEL_SIZE}>
          <Typography variant="body1" color="text.primary">
            Project Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define the core project details and identifiers.
          </Typography>
        </Grid>

        <Grid size={SECTION_CONTENT_SIZE}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: isEdit ? 8 : 12 }}>
              <FieldControl
                variant="input"
                keyName="title"
                label="Title"
                value={formData.title}
                onChange={handleFieldChange}
                error={!!fieldErrors.title}
                helperText={fieldErrors.title}
                required
              />
            </Grid>

            {isEdit && (
              <Grid size={{ xs: 12, md: 4 }}>
                <FieldControl
                  variant="select"
                  keyName="project_status"
                  label="Status"
                  value={formData.project_status}
                  options={getFilterOptions("PROJECT_STATUS").map((value) => ({
                    value: value.key,
                    label: value.display_text,
                  }))}
                  onChange={handleFieldChange}
                  error={!!fieldErrors.status}
                  helperText={fieldErrors.status}
                  required
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <FieldControl
                variant="outlined"
                keyName="description"
                label="Description"
                value={formData.description}
                onChange={handleFieldChange}
                error={!!fieldErrors.description}
                helperText={fieldErrors.description}
                multiline
                rows={3}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <AutoComplete
                value={formData.programme}
                onSelect={(value) => handleFieldChange("programme", value)}
                name="programme"
                label="Programme"
                error={!!fieldErrors.programme}
                helperText={fieldErrors.programme}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FieldControl
                keyName="portfolio"
                label="Portfolio"
                variant="select"
                value={formData.portfolio}
                options={filteredPortfolios.filter((p: { programme_id: number; }) => {
                  if (formData.programme) {
                    return p.programme_id === Number(formData.programme);
                  }
                  return true;
                }).map((p: { portfolio_name: any; id: any; }) => ({
                  label: p.portfolio_name,
                  value: p.id,
                }))}
                onChange={handleFieldChange}
                error={!!fieldErrors.portfolio}
                helperText={fieldErrors.portfolio}
                required
              />
            </Grid>

            {!hiddenProjectFormFields.includes("g17") && (
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldControl
                keyName="g_17"
                label="G17"
                variant="input"
                value={formData.g_17}
                onChange={handleFieldChange}
                error={!!fieldErrors.g_17}
                helperText={fieldErrors.g_17}
              />
            </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <FieldControl
                keyName="project_scope"
                label="Project Scope"
                variant="outlined"
                multiline
                rows={3}
                value={formData.project_scope}
                onChange={handleFieldChange}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <AutoComplete
                value={formData.org}
                onSelect={(value) => handleFieldChange("org", value)}
                name="network"
                label="Organisation"
                error={!!fieldErrors.org}
                helperText={fieldErrors.org}
                disabled={isEdit}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <AutoComplete
                value={formData.region}
                onSelect={(value) => handleNetworkChange(value)}
                name="region"
                label="Network"
                error={!!fieldErrors.region}
                helperText={fieldErrors.region}
                disabled={isEdit}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <AutoComplete
                value={formData.workstream}
                onSelect={(value) => handleFilterChange("workstream", value)}
                name="workstream"
                label="Workstream"
                error={!!fieldErrors.workstream}
                helperText={fieldErrors.workstream}
                disabled={isEdit}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FieldControl
                keyName="work_type_group"
                label="Work Type Group"
                variant="select"
                value={formData.work_type_group}
                options={work_type_groups.map((w) => ({
                  label: w.name,
                  value: w.id,
                }))}
                onChange={(_key: string, value: string[]) =>
                  handleFilterChange("work_type_group", value)
                }
                error={!!fieldErrors.work_type_group}
                helperText={fieldErrors.work_type_group}
                disabled={isEdit}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FieldControl
                keyName="work_type"
                label="Work Type"
                variant="select"
                value={formData.work_type}
                options={work_types.map((w) => ({
                  label: w.name,
                  value: w.id,
                }))}
                onChange={handleFieldChange}
                error={!!fieldErrors.work_type}
                helperText={fieldErrors.work_type}
                disabled={isEdit}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FieldControl
                keyName="wbs_code_id"
                label="WBS Code"
                variant="input"
                value={formData.wbs_code_id}
                onChange={handleFieldChange}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FieldControl
                keyName="project_budget"
                label="Project Budget"
                variant="input"
                value={formData.project_budget}
                onChange={handleFieldChange}
                error={!!fieldErrors.project_budget}
                helperText={fieldErrors.project_budget}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FieldControl
                keyName="project_income"
                label="Project Income"
                variant="input"
                value={formData.project_income}
                onChange={handleFieldChange}
                error={!!fieldErrors.project_income}
                helperText={fieldErrors.project_income}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 12 }}>
              <FieldControl
                keyName="project_year"
                label="Reporting Period"
                variant="select"
                options={getFilterOptions('REPORTING_YEAR').map((opt: any) => ({ value: opt.key, label: opt.display_text }))}
                value={formData.project_year}
                onChange={handleFieldChange}
                error={!!fieldErrors.project_year}
                helperText={fieldErrors.project_year}
                required
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Location & Site Details Section */}
      <Grid container spacing={3}>
        <Grid size={SECTION_LABEL_SIZE}>
          <Typography variant="body1" color="text.primary">
            Location & Site Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Specify geographic and operational site information.
          </Typography>
        </Grid>

        <Grid size={SECTION_CONTENT_SIZE}>
          <Grid container>
            <Grid size={{ xs: 12, md: 12 }}>

              <AsyncAutoComplete
                label={otherSite ? "Other Site" : "Site Name"}
                value={formData.site_name || ""}
                onChange={(value) => {
                  const selectedSite = sites.find((site: any) => site.id === value);
                  handleSiteChange("site_name", value, selectedSite);
                }}
                currentOption={currentSiteOption}
                options={sites.map((site: any) => ({
                  label: site.description,
                  value: site.id,
                }))}
                onSearch={(text) => setSearch(text)}
                onScrollEnd={() => {
                  if (hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                  } 
                }}
                loading={isSitesLoading || isFetchingNextPage}
                error={!!fieldErrors.site_name}
                helperText={
                  !formData.region 
                    ? "Please select a Network" 
                    : fieldErrors.site_name 
                    ? fieldErrors.site_name
                    : sites.length === 0 && !isSitesLoading ? "No sites available" : ""
                }
                disabled={!formData.region}
                required
              />

              <Box
                display="flex"
                alignItems="center"
                justifyContent={"space-between"}
              >
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={otherSite}
                    onChange={handleOtherSiteChange}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Choose Other Site
                  </Typography>
                </Box>
                <Button
                  startIcon={<Add />}
                  onClick={() => {
                    setNewSiteDialogOpen(true);
                    setOtherSite(true);
                    setFormData((prev) => ({
                      ...prev,
                      site_name: null,
                      address: null,
                      what3_words: null,
                      lat: null,
                      long: null,
                    }));
                  }}
                  disabled={!otherSite}
                >
                  ADD NEW SITE
                </Button>
              </Box>
            </Grid>
            {formData.site_name && formData.region && (
              <Grid size={{ xs: 12, md: 12 }} sx={{ height: 500, mt: 1 }}>
                <Map
                  network={formData.region}
                  initialLocation={{
                    address: formData.address || "",
                    what3_words: formData.what3_words || "",
                    coordinates:
                      formData.lat && formData.long
                        ? ([formData.long, formData.lat] as [number, number])
                        : null,
                  }}
                  onLocationChange={(location) => {
                    handleAddressChange({
                      address: location.address,
                      what3_words: location.what3_words,
                      lat: location.coordinates[1],
                      long: location.coordinates[0],
                    });
                  }}
                  disabled={isEdit}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {!isEdit && !hideProjectAssetDetails && (
        <>
          {/* Asset Details Section */}
          <Grid container spacing={3}>
            <Grid size={SECTION_LABEL_SIZE}>
              <Typography variant="body1" color="text.primary">
                Asset details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Link assets or deliverables associated with this project.
              </Typography>
            </Grid>

            <Grid size={SECTION_CONTENT_SIZE}>
              {assets.map((asset, index) => (
                <Card key={asset.id} sx={{ mb: 2 }}>
                  <AssetHeader
                    title={`Asset ${index + 1}`}
                    onDelete={() => handleRemoveAsset(asset.id)}
                  />

                  <Grid container spacing={2} p={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AutoComplete
                        value={asset.asset_programme}
                        onSelect={(value) =>
                          handleAssetChange(asset.id, "asset_programme", value)
                        }
                        name="programme"
                        label="Programme"
                        error={!!assetErrors[asset.id]?.asset_programme}
                        helperText={assetErrors[asset.id]?.asset_programme}
                        required
                      />
                    </Grid>

                    {asset.asset_programme && asset.asset_programme == 6 && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FieldControl
                          keyName="narms_value"
                          label="NARMS Value"
                          variant="input"
                          value={asset.narms_value}
                          onChange={(_key: string, value: any) =>
                            handleAssetChange(asset.id, "narms_value", value)
                          }
                          error={!!assetErrors[asset.id]?.narms_value}
                          helperText={assetErrors[asset.id]?.narms_value}
                        />
                      </Grid>
                    )}
                    {!hiddenProjectFormFields.includes("hc_code") && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <AutoComplete
                          value={asset.iln_number_id}
                          onSelect={(value) =>
                            handleAssetChange(asset.id, "iln_number_id", value)
                          }
                          name="iln"
                          label="HCode"
                          error={!!assetErrors[asset.id]?.iln_number_id}
                          helperText={assetErrors[asset.id]?.iln_number_id}
                          required
                        />
                      </Grid>
                    )}

                    <Grid size={{ xs: 12, md: 6 }}>
                      <FieldControl
                        keyName="functional_location"
                        label="Functional Location"
                        variant="input"
                        value={asset.functional_location}
                        onChange={(_key: string, value: any) =>
                          handleAssetChange(
                            asset.id,
                            "functional_location",
                            value,
                          )
                        }
                        error={!!assetErrors[asset.id]?.functional_location}
                        helperText={assetErrors[asset.id]?.functional_location}
                        required
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <FieldControl
                        keyName="sap_hierarchy"
                        label="SAP Hierarchy"
                        variant="input"
                        value={asset.sap_hierarchy}
                        onChange={(_key: string, value: any) =>
                          handleAssetChange(asset.id, "sap_hierarchy", value)
                        }
                        error={!!assetErrors[asset.id]?.sap_hierarchy}
                        helperText={assetErrors[asset.id]?.sap_hierarchy}
                        required
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <FieldControl
                        keyName="asset_budget"
                        label="Asset Budget"
                        variant="input"
                        value={asset.asset_budget}
                        onChange={(_key: string, value: any) =>
                          handleAssetChange(asset.id, "asset_budget", value)
                        }
                        error={!!assetErrors[asset.id]?.asset_budget}
                        helperText={assetErrors[asset.id]?.asset_budget}
                        required
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <FieldControl
                        keyName="asset_category"
                        label="Asset Category"
                        variant="select"
                        value={asset.asset_category}
                        options={getFilterOptions("ASSET_CATEGORY").map(
                          (option) => ({
                            label: option.display_text,
                            value: option.key,
                          }),
                        )}
                        onChange={(_key: string, value: any) =>
                          handleAssetChange(asset.id, "asset_category", value)
                        }
                        error={!!assetErrors[asset.id]?.asset_category}
                        helperText={assetErrors[asset.id]?.asset_category}
                        required
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <FieldControl
                        keyName="asset_type"
                        label="Asset Type"
                        variant="select"
                        value={asset.asset_type}
                        options={getFilterOptions("ASSET_TYPE")
                          .filter((a) => {
                            return asset.asset_category === a.parent_key;
                          })
                          .map((option) => ({
                            label: option.display_text,
                            value: option.key,
                          }))}
                        onChange={(_key: string, value: any) =>
                          handleAssetChange(asset.id, "asset_type", value)
                        }
                        error={!!assetErrors[asset.id]?.asset_type}
                        helperText={assetErrors[asset.id]?.asset_type}
                        required
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <FieldControl
                        keyName="asset_description"
                        label="Asset Description"
                        variant="input"
                        value={asset.asset_description}
                        onChange={(_key: string, value: any) =>
                          handleAssetChange(
                            asset.id,
                            "asset_description",
                            value,
                          )
                        }
                        error={!!assetErrors[asset.id]?.asset_description}
                        helperText={assetErrors[asset.id]?.asset_description}
                        required
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}
              <Button
                variant="text"
                startIcon={<Add  />}
                onClick={handleAddAsset}
                sx={{ color: "primary", alignSelf: "start" }}
              >
                ADD ASSET
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Schedule Details Section */}
          <Grid container spacing={3}>
            <Grid size={SECTION_LABEL_SIZE}>
              <Typography variant="body1" color="text.primary">
                Schedule details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Capture key project timeline and Schedule.
              </Typography>
            </Grid>

            <Grid container spacing={2} sx={{ flex: 1 }}>
              <Grid size={{ xs: 12 }}>
                <FieldControl
                  keyName="planned_start_date"
                  label="Planned Start Date"
                  variant="date"
                  value={formData.planned_start_date}
                  onChange={handleFieldChange}
                  error={!!fieldErrors.planned_start_date}
                  helperText={fieldErrors.planned_start_date}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldControl
                  keyName="planned_commissioning_date"
                  label="Planned Commissioning Date"
                  variant="date"
                  value={formData.planned_commissioning_date}
                  onChange={handleFieldChange}
                  minDate={formData.planned_start_date}
                  error={!!fieldErrors.planned_commissioning_date}
                  helperText={fieldErrors.planned_commissioning_date}
                  disabled={!formData.planned_start_date}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FieldControl
                  keyName="planned_close_up_date"
                  label="Planned Close Out date"
                  variant="date"
                  value={formData.planned_close_up_date}
                  onChange={handleFieldChange}
                  minDate={formData.planned_commissioning_date}
                  error={!!fieldErrors.planned_close_up_date}
                  helperText={fieldErrors.planned_close_up_date}
                  disabled={!formData.planned_commissioning_date}
                  required
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      <Grid container spacing={1.5} justifyContent="flex-end" mt={2}>
        <Button
          variant="outlined"
          color="primary"
          disabled={isSubmitting}
          onClick={goBack}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? `${isEdit ? "Updating" : "Creating"} Project...`
            : `${isEdit ? "Update" : "Create"} Project`}
        </Button>
      </Grid>

      <DialogBox
        showModal={newSiteDialogOpen}
        title="Add New Site"
        subTitle=""
        onModalClose={() => setNewSiteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        children={
          <SiteDialog
            formData={formData}
            onSuccess={(site) => {
              setNewSiteDialogOpen(false);
              if (site?.id) {
                setOtherSite(true);
              }
              setFormData((prev) => ({
                ...prev,
                org: site.org_id,
                region: site.network_id,
                site_name: site.id,
                site_description: site.description,
                address: site.address,
                what3_words: site.what3_words,
                lat: site.latitude,
                long: site.longitude
              }));
              refetchThirdPartySites();
              refetchSites();
            }}
            onCancel={() => {
              setNewSiteDialogOpen(false);
            }}
          />
        }
      />
    </Box>
  );
};


// ?isOpen=true&objectType=stage_gate&objectId=85734&projectId=3030&taskId=106269&id=85734&type=stageGate

// ?isOpen=true&objectType=stage_gate&objectId=77300&projectId=3030&taskId=79038&id=77300&type=stageGate